import { Component } from 'react';
import PropTypes from 'prop-types';

import { withRouter } from 'core/libs/router';
import { debounce } from 'core/libs/lodash';
import { compose } from 'core/libs/recompose';

import { bebop as bebopApi } from 'core/api/definitions/bebop';

import { denormalizeData } from 'core/utils/api';
import { getRecommendations } from 'core/utils/rcm';
import resolveRelationships from 'core/utils/relationships';
import { getCookie } from 'core/utils/cookie-helper';
import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';
import { isInViewport } from 'core/utils/dom';

import withUser from 'core/hocs/withUser';
import withRequestId from 'core/hocs/withRequestId';
import withGlobalTopicContext from 'core/hocs/withGlobalTopicContext';
import withQueryClient from 'core/hocs/withQueryClient';
import TopicsMeta from 'core/components/Topic/TopicsMeta';
import QuickEditLink from 'core/components/Topic/QuickEditLink';
import { SimpleLoader } from 'core/components/Loader';
import { checkScrollToSiteInTopic } from 'core/components/ScrollToSite';

import Item from './Item';

import { INFINITY_SCROLL_TOPIC_TYPES, INFINITY_TOPICS_LIMIT, USER_ID_COOKIE } from 'core/site/constants';


class InfiniteTopic extends Component {
  static propTypes = {
    /**
     * Рендер для топиков
     */
    children: PropTypes.func.isRequired,

    /**
     * Данные текущего, первого топика
     */
    content: PropTypes.object.isRequired,

    /**
     * Используется в BaseTopic.
     *
     * Получаем из withRequestId и пробрасываем дальше.
     *
     * Получать `requestId` из HOC в BaseTopic нельзя, из-за того что BaseTopic перерендерится раньше и со старым топиком.
     */
    requestId: PropTypes.string.isRequired,

    /** id блока рекомендации */
    rcmBlockId: PropTypes.string,

    /**
     * Функция для генерации тайтла станицы.
     * Используется для сложных SEO шаблонов, зависящих от рубрики, типа топика, пагинации и прочего.
     */
    titleBuilder: PropTypes.func,

    /** @ignore */
    user: PropTypes.object,

    /** @ignore */
    location: PropTypes.object.isRequired,

    /** @ignore */
    history: PropTypes.object.isRequired,

    /**
     * Для скрытия амп-ссылки в мета-тегах `link[rel='amphtml']`.
     */
    hideAmpLink: PropTypes.bool,

    queryClient: PropTypes.object,
  };

  static contextTypes = {
    requestHeaders: requestHeadersPropTypes,
  };

  static defaultProps = {
    children: () => null,
    topic_type: INFINITY_SCROLL_TOPIC_TYPES,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.requestId === prevState.requestId) {
      return null;
    }

    /**
     * Если обновляется requestId, то это означает, что произошел переход
     * ссылке на другой топик.
     */
    return {
      requestId: nextProps.requestId,
      content: [nextProps.content],
      activeIndex: 0,
      initialSearchParams: nextProps.location.search,
    };
  }

  state = {
    requestId: this.props.requestId,
    content: [this.props.content],
    loading: false,
    activeIndex: 0,
    initialSearchParams: this.props.location.search,
  };

  componentDidMount() {
    this.observer = new IntersectionObserver(async entries => {
      const {
        loading,
        content,
      } = this.state;

      /**
       * Ограничиваем подгрузку топиков.
       */
      if (content.length >= INFINITY_TOPICS_LIMIT || loading) return;

      if (entries.some(entry => entry.isIntersecting)) {
        this.loadNewTopic();
      }
    }, {
      /**
       * Начинаем подгрузку следующего топика за 1 экрана.
      */
      rootMargin: '100% 0%',
    });

    this.trigger && this.observer.observe(this.trigger);
    window.addEventListener('scroll', this.endOfTopicReachedHandler);
  }

  componentWillUnmount() {
    this.observer && this.observer.disconnect();
    window.removeEventListener('scroll', this.endOfTopicReachedHandler);
    this.unmounted = true;
  }

  loadNewTopic = () => {
    this.setAtomicState({ loading: true });

    this.loadNextTopic()
      .then(denormalizeData)
      .then(topic => {
        this.setAtomicState(state => ({
          content: state.content.concat(topic),
          loading: false,
        }));
      });
  };

  endOfTopicReachedHandler = debounce(() => {
    const trigger = this.trigger;
    if (trigger && isInViewport(trigger)) {
      if (this.state.content.length < INFINITY_TOPICS_LIMIT) {
        this.loadNewTopic();
        return;
      }
      window.removeEventListener('scroll', this.endOfTopicReachedHandler);
    }
  }, 1000);

  /**
   * Здесь храним информацию о том, откуда топики получены:
   * из листа, из плагина, из рекомендаций или просто последний топик
   * Не делаем через стейт, чтобы не дергать лишний раз рендер.
   * Кроме того, для рекомендаций сохраняем там recItem для дальнейшей его передачи
   * в события рекомендалки
   */
  contentSources = ['initial'];
  addContentSource(source) {
    this.contentSources = [...this.contentSources, source];
  }

  rcmSessionId = null;

  /**
   * Подгружает топики в бесконечном скроле
   */
  async loadNextTopic() {
    const {
      content,
    } = this.state;

    const {
      /* eslint-disable */
      children,
      content: initialTopic,
      location,
      history,
      requestId,
      titleBuilder,
      user,
      match,
      router,
      staticContext,
      /* eslint-enable */
      rcmBlockId,
      queryClient,
      ...getTopicsParams
    } = this.props;

    const excludedIds = content.map(item => item.id);

    /**
     * Для заходного топика первым делом подгружаем данные из листа "Следущий топик"
     */
    if (content.length === 1) {
      const globalNextTopicList = await queryClient.fetchQuery({
        queryKey: ['bebopApi', 'getTopics', {
          list: 'scroll_to_topic',
          limit: 1,
          excluded_ids: excludedIds,
          include: 'all',
        }],
        queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params),
      });

      if (globalNextTopicList?.data?.length > 0) {
        this.addContentSource({ type: 'scroll_to_topic_list' });
        return globalNextTopicList;
      }
    }

    /**
     * Дальше подгружаем данные из плагина scroll_to_topic
     */
    const relationships = resolveRelationships(['scroll_to_topic'], {});

    const {
      scroll_to_topic: {
        topic_id: scrollToTopicId,
      },
    } = relationships(content[content.length - 1]);

    if (scrollToTopicId && !excludedIds.includes(scrollToTopicId)) {
      const scrollToTopic = await queryClient.fetchQuery({
        queryKey: ['bebopApi', 'getTopics', {
          ids: scrollToTopicId,
          include: 'all',
        }],
        queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params),
      });

      this.addContentSource({ type: 'scroll_to_topic_plugin' });

      return scrollToTopic;
    }

    /**
     * Дальше получаем топик из рекоммендаций
     */
    if (rcmBlockId) {
      try {
        const recommendation = await this.loadRcm(excludedIds, getTopicsParams);

        if (recommendation?.topic) {
          this.addContentSource({ type: 'rcm', recItem: recommendation.recItem });
          return recommendation.topic;
        }
      } catch (e) {
        console.error(e);
      }
    }

    /**
     * В конце берем последний топик
     */
    this.addContentSource({ type: 'regular_topic' });

    return await queryClient.fetchQuery({
      queryKey: ['bebopApi', 'getTopics', {
        ...getTopicsParams, // TODO: 11158 здесь в запрос могут попадать ненужные параметры
        limit: '1',
        excluded_ids: excludedIds,
        include: 'all',
      }],
      queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params),
    });
  }

  async loadRcm(excludedIds, topicsParams) {
    const {
      content,
      rcmBlockId,
      user,
      queryClient,
    } = this.props;

    const {
      requestHeaders: {
        host,
      },
    } = this.context;

    const recommendation = await getRecommendations({
      blockId: rcmBlockId,
      limit: 1,
      item_id: content.id,
      user,
      host,
      xuid: getCookie(USER_ID_COOKIE),
      item_excluded_ids: excludedIds.join(),
      ...this.rcmSessionId && { session_id: this.rcmSessionId },
    });

    this.rcmSessionId = recommendation?.data?.session_id;

    const recItem = recommendation?.data?.recommendations[0];

    if (!recItem?.item_id) return null;

    const topic = await queryClient.fetchQuery({
      queryKey: ['bebopApi', 'getTopics', {
        ...topicsParams, // TODO: 11158 здесь в запрос могут попадать ненужные параметры
        limit: 1,
        ids: recItem.item_id,
        include: 'all',
      }],
      queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params),
    });

    if (topic?.data?.length === 0) return null;

    return {
      topic,
      recItem,
    };
  }

  setAtomicState(...args) {
    if (!this.unmounted) {
      this.setState(...args);
    }
  }

  setActive = index => debounce(() => {
    if (this.unmounted) return;

    const { activeIndex, content, initialSearchParams } = this.state;
    const activeTopic = content[index];

    /**
     * Вторым параметром передаем объект с флагом, который используется
     * для того, чтобы не отскролливать наверх страницу и чтобы не
     * передергивать имеющиеся на странице банеры (не обновлять requestId)
     */
    if (activeTopic?.attributes && activeIndex !== index) {
      this.setAtomicState({ activeIndex: index });
      const { link } = activeTopic.attributes;
      /**
       * Для первого топика подставляем начальные гет параметры.
       * это нужно для правильной работы пагинации в статье
       */
      const nextUrl = index === 0 ? link + initialSearchParams : link;
      this.props.history.replace(nextUrl, { infinity: true });
    }
  }, 1000);

  createRef = el => {
    this.trigger = el;
  };

  render() {
    const { children, rcmBlockId, titleBuilder, hideAmpLink } = this.props;
    const { content, loading, activeIndex, requestId } = this.state;

    // Если встречаем scrollToSite, то отключаем подгрузку топиков
    const disableScroll = checkScrollToSiteInTopic({ content: content[0] });
    const activeTopic = content[activeIndex];

    return (
      <>
        <TopicsMeta
          hideAmpLink={hideAmpLink}
          content={activeTopic}
          titleBuilder={titleBuilder}
        />
        {content.map((topic, index) => (
          <Item
            contextItemId={this.props.content.id}
            key={topic.id + requestId}
            requestId={requestId}
            topic={topic}
            infinityIndex={index}
            setActive={this.setActive(index)}
            recItem={this.contentSources[index].recItem}
            rcmBlockId={rcmBlockId}
            rcmSessionId={this.rcmSessionId}
          >
            {children}
          </Item>
        ))}
        {loading && <SimpleLoader />}
        {!disableScroll && <div ref={this.createRef} style={{ height: 1 }} />}
        <QuickEditLink topicId={activeTopic.id} />
      </>
    );
  }
}

const InfiniteTopicWithHOCs = compose(
  withQueryClient,
  withRouter,
  withRequestId,
  withGlobalTopicContext,
  withUser,
)(InfiniteTopic);
InfiniteTopicWithHOCs.displayName = 'InfiniteTopic';

export { InfiniteTopic as StorybookComponent };
export default InfiniteTopicWithHOCs;
